import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import normalize from 'json-api-normalizer'
import Jsona from 'jsona'
import { Loader2 } from 'lucide-react'

import { Label } from '../../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'

const jsonApiDataFormatter = new Jsona()

const SkillLevelSelect = React.forwardRef(
  (
    {
      name = 'the student',
      helpText = `If you don't know, we will auto-place ${name} into a level based on their age.`,
      value,
      onChange,
      error
    },
    ref
  ) => {
    const [options, setOptions] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const params = useParams()
    const { facility_id } = params

    useEffect(() => {
      let mounted = true
      setIsLoading(true)

      axios
        .get(`/api/facilities/${facility_id}/skill_levels`)
        .then((response) => {
          if (!mounted) return

          const skillLevels = jsonApiDataFormatter.denormalizeReduxObject({
            reduxObject: normalize(response.data),
            entityType: 'skillLevel'
          })

          if (!skillLevels || !Array.isArray(skillLevels)) {
            setOptions([{ label: "I don't know", value: '-1' }])
            setIsLoading(false)
            return
          }

          const options = skillLevels.map(({ name, id }) => ({
            label: name,
            value: id
          }))
          options.unshift({ label: "I don't know", value: '-1' })
          setOptions(options)
          setIsLoading(false)
        })
        .catch(() => {
          if (!mounted) return
          setOptions([{ label: "I don't know", value: '-1' }])
          setIsLoading(false)
        })

      return () => (mounted = false)
    }, [facility_id])

    return (
      <div ref={ref}>
        <Label htmlFor="skillLevel">What is the level {name} is working on?</Label>
        {helpText && <p className="text-xs text-gray-500 mb-2">{helpText}</p>}
        <Select value={value || ''} onValueChange={onChange} disabled={isLoading}>
          <SelectTrigger className={`mt-2 ${error ? 'border-red-500' : ''}`}>
            {isLoading ? (
              <div className="flex items-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </div>
            ) : (
              <SelectValue placeholder="Select Level" />
            )}
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    )
  }
)

SkillLevelSelect.displayName = 'SkillLevelSelect'

SkillLevelSelect.propTypes = {
  name: PropTypes.string,
  helpText: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  error: PropTypes.string
}

export default SkillLevelSelect
